import { useEffect, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Modal } from '../components/Modal';
import { listOpenIssues, resolveTickIssue, type OpenIssue, getSite, type Site } from '../lib/db';
import { TICK_LISTS, flattenItems } from '../data/tickListTemplates';
import { supabase } from '../lib/supabase';
import { LoadingIndicator } from '../components/LoadingIndicator';

function fmtAge(days: number): { label: string; tone: string } {
  if (days <= 0) return { label: 'Today', tone: 'pending' };
  if (days < 3) return { label: `${days} day${days === 1 ? '' : 's'} ago`, tone: 'warn' };
  return { label: `${days} days ago`, tone: 'danger' };
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export function OpenIssues() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const [site, setSite] = useState<Site | null>(null);
  const [issues, setIssues] = useState<OpenIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [target, setTarget] = useState<OpenIssue | null>(null);
  const [resolverName, setResolverName] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    if (!siteId) return;
    setLoading(true);
    try {
      const [s, list] = await Promise.all([getSite(siteId), listOpenIssues(siteId)]);
      setSite(s);
      // Map item_key -> human label using tick list templates
      const labelByKey = new Map<string, string>();
      for (const tpl of Object.values(TICK_LISTS)) {
        for (const it of flattenItems(tpl)) labelByKey.set(it.key, it.text);
      }
      setIssues(list.map((i) => ({ ...i, item_label: labelByKey.get(i.item_key) ?? i.item_key })));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      if (name && !resolverName) setResolverName(name);
    });
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function onResolve(e: FormEvent) {
    e.preventDefault();
    if (!target) return;
    if (!resolverName.trim() || !resolutionNotes.trim()) {
      setError('Please enter your name and what was done to resolve.');
      return;
    }
    setSubmitting(true);
    try {
      await resolveTickIssue(target.run_id, target.item_key, resolverName.trim(), resolutionNotes.trim());
      setTarget(null);
      setResolutionNotes('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell title="Open issues" back={`/sites/${siteId ?? ''}`}>
      {site && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ margin: 0 }}><strong>{site.name}</strong></p>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
            Outstanding issues from recent tick list inspections. Resolve them as they get fixed.
          </p>
        </div>
      )}

      {error && <div className="banner banner-error">{error}</div>}
      {loading && <LoadingIndicator />}

      {!loading && issues.length === 0 && (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No open issues. Nothing outstanding from recent tick lists.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginTop: 12 }}
            onClick={() => nav(`/sites/${siteId}/checks`)}
          >
            Back to checks
          </button>
        </div>
      )}

      <div className="doc-list">
        {issues.map((iss) => {
          const age = fmtAge(iss.age_days);
          return (
            <div key={`${iss.run_id}-${iss.item_key}`} className="card" style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong>{iss.item_label}</strong>
                    <span className={`status-pill status-${age.tone}`}>{age.label}</span>
                    <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>{iss.run_kind} check</span>
                  </div>
                  {iss.notes && (
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>{iss.notes}</p>
                  )}
                  <p className="muted" style={{ margin: '4px 0 0', fontSize: 11 }}>
                    Raised {fmtDateTime(iss.run_at)}
                    {iss.photo_paths && iss.photo_paths.length > 0 && ` · ${iss.photo_paths.length} photo${iss.photo_paths.length === 1 ? '' : 's'}`}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => { setTarget(iss); setResolutionNotes(''); }}
                >
                  Resolve
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {target && (
        <Modal open={!!target} title="Resolve issue" onClose={() => !submitting && setTarget(null)}>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>
            <strong>{target.item_label}</strong>
          </p>
          {target.notes && (
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
              Original note: {target.notes}
            </p>
          )}
          <form onSubmit={onResolve}>
            <div className="field needs-input">
              <label htmlFor="r-by">Resolved by *</label>
              <input
                id="r-by"
                type="text"
                value={resolverName}
                onChange={(e) => setResolverName(e.target.value)}
                placeholder="Your name"
                required
              />
            </div>
            <div className="field needs-input">
              <label htmlFor="r-notes">What was done? *</label>
              <textarea
                id="r-notes"
                rows={3}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="e.g. ACME Scaffolding replaced toe-board on 09/05. Inspected and tagged."
                required
              />
            </div>
            <div className="btn-stack">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Resolving…' : 'Mark resolved'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setTarget(null)} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}
